// API client for Knowledge Documents (separate `knowledge_docs` collection).
// Endpoints accept PROJECT_READ_PERMISSION so read-only members (Ask-mode viewers)
// can save, edit, and share the docs they generate.
import api from '../utils/axios';
import { filenameFromContentDisposition } from '../utils/fileUtils';
import { parseKnowledgeDocs, KnowledgeDoc } from '../utils/parseKnowledgeDocs';
import type { BuildArtifact } from '../types/chat';

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export interface KnowledgeDocRecord {
  id?: string;
  _id?: string;
  project_id: string;
  title: string;
  content: string;
  type: string;
  knowledge_doc_id?: string;
  version?: string;
  created_at?: string;
  updated_at?: string;
  size?: number;
  message_id?: string;
  thread_id?: string;
  created_from?: string;
  last_accessed_at?: string;
  readonly_sharable_link_enabled?: boolean;
  created_by?: string; // email of the user who first saved the doc
  updated_by?: string; // email of the last user to save/toggle
}

export interface SaveKnowledgeDocInput {
  projectId: string;
  knowledgeDocId: string; // stable id from the <KnowledgeDoc id="..."> tag
  title: string;
  content: string;
  version?: string;
  messageId?: string;
  threadId?: string;
  createdFrom?: 'ask' | 'agent' | 'build';
}

export async function saveKnowledgeDoc(input: SaveKnowledgeDocInput): Promise<KnowledgeDocRecord> {
  const size = new Blob([input.content || '']).size;
  const response = await api.post(
    `/projects/${input.projectId}/knowledge-docs/`,
    {
      project_id: input.projectId,
      knowledge_doc_id: input.knowledgeDocId,
      title: input.title,
      content: input.content || '',
      type: 'text/markdown',
      version: input.version,
      message_id: input.messageId,
      thread_id: input.threadId,
      created_from: input.createdFrom || 'ask',
      size,
    },
    { headers: authHeader() },
  );
  return response.data;
}

export async function listKnowledgeDocs(projectId: string): Promise<KnowledgeDocRecord[]> {
  const response = await api.get(`/projects/${projectId}/knowledge-docs/`, { headers: authHeader() });
  return response.data || [];
}

export async function getKnowledgeDoc(projectId: string, docId: string): Promise<KnowledgeDocRecord> {
  // Token is optional — public docs with readonly_sharable_link_enabled don't require it.
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  const response = await api.get(`/projects/${projectId}/knowledge-docs/${docId}`, { headers });
  return response.data;
}

export interface DownloadKnowledgeDocInput {
  projectId: string;
  title: string;
  content?: string;
  docId?: string; // saved Mongo _id; the server serves the stored copy when given
}

/**
 * Download a knowledge doc as a .md file through the download endpoint, which requires
 * DOWNLOAD_PERMISSION. A saved doc is served from the database; an unsaved one is echoed
 * from `content`, so a doc that only exists in a chat message can be downloaded too.
 */
export async function downloadKnowledgeDoc(input: DownloadKnowledgeDocInput): Promise<void> {
  const token = localStorage.getItem('token');
  const response = await api.post(
    `/projects/${input.projectId}/knowledge-docs/download-with-temp`,
    { title: input.title, content: input.content || '' },
    {
      params: input.docId ? { knowledge_doc_id: input.docId } : undefined,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      responseType: 'blob',
    },
  );

  const safeTitle = (input.title || 'knowledge-document').trim();
  const fallback = safeTitle.toLowerCase().endsWith('.md') ? safeTitle : `${safeTitle}.md`;
  const filename = filenameFromContentDisposition(response.headers['content-disposition'], fallback);

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/markdown' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function updateKnowledgeDoc(
  projectId: string,
  docId: string,
  update: Partial<Pick<KnowledgeDocRecord, 'title' | 'content' | 'version' | 'readonly_sharable_link_enabled'>>,
): Promise<KnowledgeDocRecord> {
  const response = await api.put(`/projects/${projectId}/knowledge-docs/${docId}`, update, {
    headers: authHeader(),
  });
  return response.data;
}

export async function deleteKnowledgeDoc(projectId: string, docId: string): Promise<void> {
  await api.delete(`/projects/${projectId}/knowledge-docs/${docId}`, { headers: authHeader() });
}

// Fetch a message's raw content so an UNSAVED knowledge doc can be parsed out of it
// (preview / open-in-new-tab). The message is the durable source — no temp store needed.
export async function getMessageContent(
  projectId: string,
  messageId: string,
): Promise<{ content: string; interaction_mode: string; artifacts?: BuildArtifact[] }> {
  const response = await api.get(
    `/projects/${projectId}/messages/${messageId}/knowledge-doc-source`,
    { headers: authHeader() },
  );
  return response.data;
}

// Resolve an UNSAVED knowledge doc from its source message. A FedRAMP Build message
// lists its docs in `artifacts`; a web message carries them as <KnowledgeDoc> tags.
export async function getUnsavedKnowledgeDoc(
  projectId: string,
  messageId: string,
  identifier: string,
): Promise<{ title: string; content: string; version?: string } | null> {
  const { content, artifacts } = await getMessageContent(projectId, messageId);
  const artifact = artifacts?.find(a => a.id === identifier);
  if (artifact) {
    return {
      title: artifact.title || 'Knowledge Document',
      content: artifact.content,
      version: artifact.version,
    };
  }
  const match = parseKnowledgeDocs(content).parts.find(
    (p): p is KnowledgeDoc => typeof p !== 'string' && p.identifier === identifier,
  );
  return match ? { title: match.title, content: match.content, version: match.version } : null;
}
